"use client";

import axios from "axios";
import { useSession } from "next-auth/react";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { LuX } from "react-icons/lu";
import { GrEdit } from "react-icons/gr";
import { TbCameraPlus } from "react-icons/tb";
import { MdContentCopy } from "react-icons/md";

import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "~/components/shadcn/Avatar";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetClose,
} from "~/components/v3/shadcn/Sheet";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "~/components/v3/shadcn/Form";
import {
    Dialog,
    DialogContent,
    DialogTrigger,
} from "~/components/v3/themed/Dialog";

import { env } from "~/env.mjs";
import { cn } from "~/utils/fontsConfig";
import { APP_URL } from "~/utils/constants";
import { RouterInputs, api } from "~/utils/api";
import {
    profileFormSchema,
    profileFormSchemaCustomLimits,
    ProfileFormType,
} from "~/types/UserTypes";

import {
    getDefaultFormValues,
    socialLinkFields,
    bgBannerColors,
    chatLinks,
} from "../utility/ProfilePageUtils";
import Modal from "../v3/themed/Modal";
import Spinner from "../themed/spinner";
import { Label } from "../v3/shadcn/Label";
import { Input as InputV3 } from "../v3/themed/Input";
import { Input } from "../v4/shadcn/Input";
import { TextArea } from "../v3/themed/TextArea";
import { RadioGroup, RadioGroupItem } from "../v3/shadcn/RadioGroup";
import { ProfileEditRequiredData } from "../rsvp/validators";

import { PhoneNumberStep } from "./phone-validation-components/PhoneNumberStep";

import { Button } from "../v3/themed/Button";
import { slugifyUser } from "~/utils/string";
import { UserAvatar } from "../v4/shadcn/UserAvatar";
import { OrganizationModal } from "./OrganizationModal";
import { Checkbox } from "../shadcn/Checkbox";
import { CustomizableLayout } from "~/types/CustomizableLayout";

type Props = {
    isVisible: boolean;
    setVisible: (show: boolean) => void;
    requiredData?: ProfileEditRequiredData | null;
    onSuccess?: () => void;
    customization?: CustomizableLayout;
};

type FileType = "image" | "bannerImage" | "organizationImage";
type FormData = RouterInputs["user"]["update"];

const EditProfileModal = ({
    isVisible,
    setVisible,
    requiredData,
    onSuccess,
    customization,
}: Props) => {
    const { data: session } = useSession();
    const {
        data: user,
        isLoading: isUserLoading,
        refetch: refetchUser,
    } = api.user.getUserById.useQuery(
        { userId: session?.user.id ?? "" },
        { enabled: !!session?.user.id },
    );
    const pathname = usePathname();

    const updateUserMutation = api.user.update.useMutation();
    const isSlugTaken = api.user.isProfileSlugTaken.useMutation();
    const { mutateAsync: fetchPresignedUrls } =
        api.s3.getProfilePresignedUrl.useMutation();

    // This is required since the required Data can change after being edited
    const resolver = useMemo(() => {
        if (!requiredData) return zodResolver(profileFormSchema);
        return zodResolver(profileFormSchemaCustomLimits(requiredData));
    }, [requiredData]);

    const form = useForm<ProfileFormType>({
        defaultValues: user ? getDefaultFormValues(user) : {},
        resolver,
    });

    const router = useRouter();

    const bio = form.watch("bio");
    const firstName = form.watch("firstName");
    const lastName = form.watch("lastName");
    const avatarImage = form.watch("images.image");
    const bannerImage = form.watch("images.bannerImage");
    const organizationImage = form.watch("images.organizationImage");
    const phoneNumber = form.watch("phone");
    const profileSlug = form.watch("slug");
    const bioCharLength = 2048;
    const isDirty = form.formState.isDirty;

    const bannerStyle = bannerImage?.startsWith("http")
        ? {
              backgroundImage: `url(${bannerImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
          }
        : {
              background: bannerImage,
          };

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const fileTypeRef = useRef<FileType>("image");
    const [isUpLoading, setIsUpLoading] = useState(false);
    const [fullyLoaded, setFullyLoaded] = useState(false);
    const [showPhoneModal, setShowPhoneModal] = useState(false);

    useEffect(() => {
        if (!user || user.slug) return;
        form.setValue(
            "slug",
            slugifyUser({
                ...user,
                name: [firstName, lastName].join(" "),
            }),
        );
    }, [user, firstName, lastName]);

    useEffect(() => {
        if (user && !fullyLoaded) {
            form.reset(getDefaultFormValues(user) ?? {});
            setFullyLoaded(true);
        }
    }, [user, form.reset]);

    const onSubmit = async (data: ProfileFormType) => {
        if (!session?.user.id) {
            toast.error("Invalid session data");
            return;
        }

        if (data.slug !== user?.slug && data.slug) {
            const isTaken = await isSlugTaken.mutateAsync({
                slug: data.slug ?? "",
            });

            if (isTaken) {
                toast.error(
                    "This profile URL is already in use. Please choose a different one.",
                );

                return;
            }
        }

        const updatedData: FormData = {
            ...data,
            bannerImage: data?.images?.bannerImage,
            name: [data.firstName, data.lastName].join(" "),
        };

        toast.loading("Updating profile...", {
            toastId: "update-profile",
        });

        const payload = { ...updatedData, userId: session.user.id };

        await updateUserMutation.mutateAsync(
            {
                ...payload,
            },
            {
                onSuccess: (user) => {
                    toast.success("Profile updated successfully!", {
                        autoClose: 1000,
                    });
                    setVisible(false);
                    onSuccess?.();
                    refetchUser();

                    if (
                        user &&
                        user.slug !== session.user.slug &&
                        pathname?.includes("/profile")
                    ) {
                        router.push(`${APP_URL}/profile/${user.slug}`);
                    }
                },
                onError: () => {
                    toast.error("Error updating profile");
                },
                onSettled: () => {
                    toast.dismiss("update-profile");
                },
            },
        );
    };

    const handlePhoneSubmit = (newPhoneNumber: string) => {
        form.setValue("phone", newPhoneNumber);
        setShowPhoneModal(false);
    };

    const triggerFileUpload = (type: FileType) => {
        fileTypeRef.current = type;
        fileInputRef?.current?.click();
    };

    const updateBannerColor = (val: string) => {
        form.setValue("images.bannerImage", val);
    };

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !user) return;

        toast.loading(`Uploading picture`, {
            toastId: "upload-picture",
        });

        setIsUpLoading(true);
        const file = e.target.files[0];
        const ext = file.name.substring(file.name.lastIndexOf("."));
        const uploadFilename = `${(Math.random() + 1)
            .toString(36)
            .substring(7)}${ext}`;

        const presignedURL = await fetchPresignedUrls({
            filename: uploadFilename,
        });

        let newFile = new File([file], uploadFilename, {
            type: file.type,
        });

        try {
            await axios.put(presignedURL, newFile.slice(), {
                headers: { "Content-Type": newFile.type },
            });
            const newImage = `${env.NEXT_PUBLIC_ASSETS_URL}/profile/${user.id}/${uploadFilename}`;
            form.setValue(`images.${fileTypeRef.current}`, newImage);
            // update pic in db
            await updateUserMutation.mutateAsync(
                {
                    [fileTypeRef.current]: newImage,
                    userId: user.id,
                },
                {
                    onSuccess: () => {
                        toast.success(`Picture updated!`);
                        refetchUser();
                    },
                    onError: () => {
                        toast.error(`Error updating picture!`);
                    },
                    onSettled: () => {
                        toast.dismiss(`upload-picture`);
                    },
                },
            );
        } catch (error) {
            toast.error("Error uploading profile picture!");
            console.error(`Error updating profile picture`, error);
        }

        setIsUpLoading(false);
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        type: "x" | "li" | "ig" | "fb",
        onChange: (value: string) => void,
    ) => {
        const inputValue = event.target.value;

        // Remove the '@' symbol from the input value
        const sanitizedValue = inputValue.replace("@", "");
        if (/^[A-Za-z0-9_.]{0,32}$/.test(sanitizedValue)) {
            onChange(sanitizedValue);
        }

        switch (type) {
            case "x":
                const matchX = sanitizedValue.match(
                    /https:\/\/(?:twitter|x)\.com\/([A-Za-z0-9_.\-\/]{1,15})?/,
                );

                if (matchX) {
                    onChange(matchX[1] as string);
                }
                return;
            case "li":
                const matchLi = sanitizedValue.match(
                    /(?:https?:\/\/(?:www\.)?)?linkedin\.com\/in\/([A-Za-z0-9_.\-\/]{1,32})(?:\/)?/,
                );
                if (matchLi) {
                    onChange(matchLi[1] as string);
                }
                return;
            case "ig":
                const matchIg = sanitizedValue.match(
                    /(?:https?:\/\/(?:www\.)?)?instagram\.com\/([A-Za-z0-9_.\-\/]{1,32})(?:\/)?/,
                );
                if (matchIg) {
                    onChange(matchIg[1] as string);
                }
                return;
            case "fb":
                const matchFb = sanitizedValue.match(
                    /(?:https?:\/\/(?:www\.)?)?facebook\.com\/([A-Za-z0-9_.\-\/]{1,32})(?:\/)?/,
                );
                if (matchFb) {
                    onChange(matchFb[1] as string);
                }
                return;
        }
    };

    const copyProfileSlug = async () => {
        await navigator.clipboard.writeText(
            `${APP_URL}/profile/${profileSlug}`,
        );

        toast.success("Profile URL copied to clipboard: ");
    };

    if (isUserLoading) {
        return (
            <Modal
                open={isVisible}
                onOpenChange={() => setVisible(false)}
                title="Edit Profile"
                description="Update your profile information"
                customization={customization}
            >
                <Spinner />
            </Modal>
        );
    }

    return (
        <Sheet open={isVisible} onOpenChange={() => setVisible(false)}>
            <SheetContent
                showCloseIcon={false}
                className={cn(
                    "z-[1000] w-full overflow-y-scroll rounded-br-md rounded-tr-md border-none bg-[#121418] pb-20 md:min-w-[450px]",
                    customization?.mainCardClassName,
                )}
            >
                {isDirty && (
                    <div
                        className={cn(
                            "fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b border-gray-800 bg-[#121418] px-6 py-4 duration-300 animate-in slide-in-from-top",
                            customization?.secondaryCardClassName,
                        )}
                    >
                        <SheetTitle
                            className={cn(
                                "text-lg",
                                customization?.labelClassName,
                            )}
                        >
                            Unsaved changes
                        </SheetTitle>
                        <Button
                            className={cn(
                                "p-1 px-4 disabled:cursor-not-allowed",
                                customization?.buttons?.default,
                            )}
                            variant="default"
                            onClick={form.handleSubmit(onSubmit)}
                            disabled={form.formState.isSubmitting}
                        >
                            Save Changes
                        </Button>
                    </div>
                )}
                <SheetHeader>
                    <div className="flex w-full items-center justify-between px-6 py-4">
                        <SheetTitle
                            className={cn(
                                "text-lg",
                                customization?.labelClassName,
                            )}
                        >
                            Edit profile
                        </SheetTitle>

                        <SheetClose
                            className={cn(
                                "focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary",
                                customization?.buttons?.secondary,
                            )}
                        >
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>
                </SheetHeader>

                <div
                    style={bannerStyle}
                    className={`relative h-36 w-full bg-gray-600 ${bannerImage}`}
                >
                    {avatarImage && (
                        <button
                            className={cn(
                                "absolute bottom-2 right-2 z-50 cursor-pointer rounded-[0.6rem] bg-black/85 p-2",
                                "ease-in active:scale-95",
                                {
                                    "cursor-not-allowed": isUpLoading,
                                },
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                triggerFileUpload("bannerImage");
                            }}
                            disabled={isUpLoading}
                        >
                            <GrEdit className="text-xs text-white" />
                        </button>
                    )}

                    <div className="absolute bottom-1 left-4 flex flex-col gap-2">
                        <div>
                            <Avatar className="relative h-[4.5rem] w-[4.5rem] overflow-visible border border-black transition-[border-color] duration-300 group-hover:border-secondary">
                                {!isUpLoading ? (
                                    <UserAvatar
                                        user={{
                                            name: user?.name,
                                            email: user?.email,
                                            image: user?.image,
                                        }}
                                        className="h-[4.5rem] w-[4.5rem]"
                                    />
                                ) : (
                                    <div className="flex w-full items-center justify-center">
                                        <Spinner className="h-5 w-5" />
                                    </div>
                                )}
                                <button
                                    className={cn(
                                        "absolute bottom-0 right-[-5px] z-50 cursor-pointer rounded-[0.6rem] bg-black/80 p-2",
                                        "active:scale-95",
                                        {
                                            "cursor-not-allowed": isUpLoading,
                                        },
                                    )}
                                    onClick={(e) => {
                                        if (isUpLoading) return;

                                        e.stopPropagation();
                                        triggerFileUpload("image");
                                    }}
                                    disabled={isUpLoading}
                                >
                                    <GrEdit className="text-xs text-white" />
                                </button>
                            </Avatar>
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={(e) => {
                                    handleFileChange(e);
                                }}
                                accept="image/png, image/gif, image/jpeg, image/webp"
                                hidden
                            />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-6 p-6">
                    <div className="flex flex-col gap-2">
                        <Label
                            variant="secondary"
                            className={cn(
                                "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                customization?.inputLabelClassName,
                            )}
                        >
                            Background image
                        </Label>

                        <div className="">
                            <RadioGroup
                                className="flex gap-1"
                                defaultValue={`${bannerImage}`}
                                onValueChange={(e) => updateBannerColor(e)}
                            >
                                <div className="flex items-center space-x-2">
                                    {bgBannerColors.map((item) => (
                                        <RadioGroupItem
                                            id={item.id}
                                            key={item.id}
                                            value={item.value}
                                            className={`h-7 w-7`}
                                            style={{ background: item.value }}
                                            showIndicatorIcon={false}
                                        />
                                    ))}
                                </div>
                            </RadioGroup>
                        </div>
                    </div>

                    <Form {...form}>
                        <form
                            className="flex flex-col gap-4"
                            onSubmit={form.handleSubmit(onSubmit)}
                        >
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            First name
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className={cn(
                                                    "rounded-[0.626rem] tracking-wide",
                                                    customization?.inputClassName,
                                                )}
                                                placeholder="Enter First Name"
                                                onBlur={field.onBlur}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Last name
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className={cn(
                                                    "rounded-[0.626rem] tracking-wide",
                                                    customization?.inputClassName,
                                                )}
                                                placeholder="Enter Last Name"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="email"
                                disabled
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Email (only visible to you)
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className={cn(
                                                    "rounded-[0.626rem] tracking-wide",
                                                    customization?.inputClassName,
                                                )}
                                                placeholder="Enter Email"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="phone"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-normal text-[#A3A8B4]",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Phone number (only visible to you)
                                        </FormLabel>
                                        <FormControl>
                                            <Dialog
                                                open={showPhoneModal}
                                                onOpenChange={setShowPhoneModal}
                                            >
                                                <DialogTrigger asChild>
                                                    <Input
                                                        {...field}
                                                        className={cn(
                                                            "cursor-pointer rounded-[0.626rem] text-left tracking-wide",
                                                            customization?.inputClassName,
                                                        )}
                                                        placeholder="Click to Enter Phone Number (optional)"
                                                    />
                                                </DialogTrigger>

                                                <DialogContent
                                                    className={cn(
                                                        "z-[1500] w-full border-none bg-[#121418] pb-20 text-white",
                                                        customization?.secondaryCardClassName,
                                                    )}
                                                    overlayClassName="z-[1500]"
                                                >
                                                    <PhoneNumberStep
                                                        user={user}
                                                        phoneNumber={
                                                            phoneNumber
                                                        }
                                                        handlePhoneSubmit={
                                                            handlePhoneSubmit
                                                        }
                                                        setShowPhoneModal={
                                                            setShowPhoneModal
                                                        }
                                                        customization={
                                                            customization
                                                        }
                                                    />
                                                </DialogContent>
                                            </Dialog>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="bio"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-normal text-[#A3A8B4]",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            About
                                        </FormLabel>
                                        <FormControl>
                                            <TextArea
                                                {...field}
                                                className={cn(
                                                    "min-h-40 w-full rounded-[0.626rem]",
                                                    customization?.inputClassName,
                                                )}
                                                placeholder="Tell me about yourself"
                                            ></TextArea>
                                        </FormControl>

                                        <FormDescription
                                            className={cn("my-1", {
                                                "text-red-500":
                                                    (bio?.length ?? 0) < 15,
                                                "text-orange-500":
                                                    (bio?.length ?? 0) >= 15 &&
                                                    (bio?.length ?? 0) < 25,
                                                "text-yellow-500":
                                                    (bio?.length ?? 0) >= 25 &&
                                                    (bio?.length ?? 0) < 30,
                                                "text-green-500":
                                                    (bio?.length ?? 0) >= 30,
                                            })}
                                        >
                                            {bio?.length ?? 0} / {bioCharLength}
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="flex flex-col gap-4">
                                <Label
                                    className={cn(
                                        "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                        customization?.inputLabelClassName,
                                    )}
                                >
                                    Chat links
                                </Label>
                                <div className="flex flex-col gap-4">
                                    {chatLinks.map((chatLink) => (
                                        <FormField
                                            control={form.control}
                                            name={chatLink.name}
                                            key={chatLink.id}
                                            render={({
                                                field: { value, ...field },
                                            }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <div className="flex gap-6">
                                                            <div className="flex items-center justify-center">
                                                                <chatLink.icon
                                                                    className={cn(
                                                                        "text-base text-white",
                                                                        customization?.inputLabelClassName,
                                                                    )}
                                                                />
                                                            </div>
                                                            <Input
                                                                className={cn(
                                                                    "w-full rounded-[0.626rem]",
                                                                    customization?.inputClassName,
                                                                )}
                                                                id={chatLink.id}
                                                                {...field}
                                                                placeholder={
                                                                    chatLink.placeholder
                                                                }
                                                                value={
                                                                    value ?? ""
                                                                }
                                                            />
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col gap-4">
                                <Label
                                    className={cn(
                                        "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                        customization?.inputLabelClassName,
                                    )}
                                >
                                    Social media handles
                                </Label>
                                <div className="flex flex-col gap-4">
                                    {socialLinkFields.map((socialLink) => (
                                        <FormField
                                            control={form.control}
                                            name={socialLink.name}
                                            key={socialLink.id}
                                            render={({
                                                field: {
                                                    onChange,
                                                    value,
                                                    ...field
                                                },
                                            }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <div className="flex gap-6">
                                                            <div className="flex items-center justify-center">
                                                                <socialLink.icon
                                                                    className={cn(
                                                                        "text-base text-white",
                                                                        customization?.inputLabelClassName,
                                                                    )}
                                                                />
                                                            </div>
                                                            <Input
                                                                className={cn(
                                                                    "w-full rounded-[0.626rem]",
                                                                    customization?.inputClassName,
                                                                )}
                                                                id={
                                                                    socialLink.id
                                                                }
                                                                {...field}
                                                                placeholder={
                                                                    socialLink.placeholder
                                                                }
                                                                value={
                                                                    value ?? ""
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) => {
                                                                    handleInputChange(
                                                                        e,
                                                                        socialLink.type,
                                                                        onChange,
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div>
                                {requiredData?.anySocialLinks && (
                                    <p className="text-sm text-red-600">
                                        At least one social link is required
                                    </p>
                                )}
                            </div>

                            <FormField
                                control={form.control}
                                name="slug"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Profile URL
                                        </FormLabel>
                                        <FormControl>
                                            <div className="flex items-center gap-3">
                                                <InputV3
                                                    {...field}
                                                    className={cn(
                                                        "col-span-2 w-full rounded-[0.626rem] tracking-wide",
                                                        customization?.inputClassName,
                                                    )}
                                                    placeholder="Enter Profile URL"
                                                    prefixComponent={
                                                        <span className="text-sm text-[#A3A8B4]">
                                                            app.getriver.io/profile/
                                                        </span>
                                                    }
                                                    suffixComponent={
                                                        <span
                                                            className="cursor-pointer px-4 text-sm"
                                                            onClick={
                                                                copyProfileSlug
                                                            }
                                                        >
                                                            <MdContentCopy />
                                                        </span>
                                                    }
                                                    isSuffixInteractive={true}
                                                />
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <OrganizationModal customization={customization}>
                                <div className="flex flex-col gap-4">
                                    <div className="flex w-full gap-4">
                                        <div className="left-4 flex flex-col gap-2">
                                            <div className="flex flex-col gap-2">
                                                <Label
                                                    className={cn(
                                                        "w-max text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                        customization?.inputLabelClassName,
                                                    )}
                                                >
                                                    Organization Image
                                                </Label>

                                                <Avatar className="relative h-[50px] w-[50px] overflow-visible border-2 border-primary transition-[border-color] duration-300 group-hover:border-secondary">
                                                    {!isUpLoading ? (
                                                        <div className="h-full w-full">
                                                            <AvatarImage
                                                                className="z-10 rounded-full"
                                                                src={
                                                                    organizationImage
                                                                }
                                                                alt="orgimage"
                                                            />
                                                            <AvatarFallback>
                                                                <TbCameraPlus className="h-7 w-7" />
                                                            </AvatarFallback>
                                                        </div>
                                                    ) : (
                                                        <div className="flex w-full items-center justify-center">
                                                            <Spinner className="h-5 w-5" />
                                                        </div>
                                                    )}
                                                    <button
                                                        className={cn(
                                                            "absolute bottom-0 right-[-5px] z-50 cursor-pointer rounded-[0.6rem] bg-black/80 p-2",
                                                            "active:scale-95",
                                                            {
                                                                "cursor-not-allowed":
                                                                    isUpLoading,
                                                            },
                                                        )}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (isUpLoading)
                                                                return;

                                                            e.stopPropagation();
                                                            triggerFileUpload(
                                                                "organizationImage",
                                                            );
                                                        }}
                                                        disabled={isUpLoading}
                                                    >
                                                        <GrEdit className="text-xs text-white" />
                                                    </button>
                                                </Avatar>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    onChange={(e) => {
                                                        handleFileChange(e);
                                                    }}
                                                    accept="image/png, image/gif, image/jpeg, image/webp"
                                                    hidden
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <FormField
                                        control={form.control}
                                        name="organizationName"
                                        render={({ field }) => (
                                            <FormItem className="w-full">
                                                <FormLabel
                                                    className={cn(
                                                        "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                        customization?.inputLabelClassName,
                                                    )}
                                                >
                                                    Organization Name
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        className={cn(
                                                            "rounded-[0.626rem] tracking-wide",
                                                            customization?.inputClassName,
                                                        )}
                                                        placeholder="Organization Name"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="organizationLink"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel
                                                    className={cn(
                                                        "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                        customization?.inputLabelClassName,
                                                    )}
                                                >
                                                    Organization URL
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        className={cn(
                                                            "rounded-[0.626rem] tracking-wide",
                                                            customization?.inputClassName,
                                                        )}
                                                        placeholder="https://example.com"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="useOrganizationIdentity"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <div className="flex items-center gap-2">
                                                        <Checkbox
                                                            checked={
                                                                field.value
                                                            }
                                                            onCheckedChange={
                                                                field.onChange
                                                            }
                                                        />
                                                        <FormLabel
                                                            className={cn(
                                                                "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                                customization?.inputLabelClassName,
                                                            )}
                                                        >
                                                            Use Organization
                                                            Identity
                                                        </FormLabel>
                                                    </div>
                                                </FormControl>

                                                <FormDescription
                                                    className={cn(
                                                        "text-sm font-normal tracking-wide text-[#A3A8B4]",
                                                        customization?.descriptionClassName,
                                                    )}
                                                >
                                                    This replaces your name for
                                                    the organization name on the
                                                    community and event pages.
                                                </FormDescription>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </OrganizationModal>

                            <div className="flex justify-end py-1">
                                <Button
                                    className={cn(
                                        "p-1 px-4 disabled:cursor-not-allowed",
                                        customization?.buttons?.default,
                                    )}
                                    variant="default"
                                    type="submit"
                                    disabled={form.formState.isSubmitting}
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default EditProfileModal;
